import { taxIdWord, taxNumberWord } from "../../../model/steuerbegriffe";
import { useMemo, useState, type FormEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router";
import { BeispielEtikett } from "../../../components/Beispiel";
import { useStaffWorkspace, type StaffWorkspace } from "../../../app/session";
import { api } from "../../../data";
import { ErrorState, Loading, NoAccess } from "../../../components/States";
import { Dialog, PillView, useAction } from "../../../components/ui";
import { CURRENCIES, selfBillingEInvoiceDecision, type SelfBillingEInvoiceReason } from "@talentkontor/domain";
import { date, initials, money, monthLabel, numberLocale, time, moneyWhole } from "../../../lib/format";
import {
  inviteTalent, removeAccess, resendInvitation, revokeInvitation, saveTalentNote, savePayout, setAssignment, setIgEnabled, type InvitationResult,
} from "./data";
import { useBase, useEntitlements, useIg, usePayout, usePlan, useTalentNote, useTeamInfo } from "./hooks";
import {
  ageOn, blockLabel, bpLabel, consentInfo, countryOptions, countryLabel, DEFAULT_PROFILE_REQUIREMENTS, fileSlug, flatTaxCsv, flatTaxEnabled, formatCount,
  igStatusPill, invitationLink, latestTotals, managersOf, metricLabel, PAYOUT_METHODS, payoutFormFrom, pill, profileGaps, shareState, smallBusinessLabel,
  ohneKursText, statusPills, sumsLabel, talentProfile, validatePayout,
  type Base, type FormErrors, type PayoutForm, type PayoutRow, type TalentProfile,
} from "./model";
import {
  BookingTable, ConfirmAction, ContractList, CopyLink, DealTable, DemoNote, Fact, HIDDEN_AMOUNTS, HIDDEN_FINANCIALS, InvoiceTable, Kpi, RankRows, RevenueLines, RIGHTS_TEXT,
  SelectField, TarifWaehlen, TextField, useSicht,
} from "./parts";
import { TalentDialog } from "./TalentDialog";
import { Abrechnung, Abzugsteuer, Exklusivitaeten, MailZustellung, Steuerstatus } from "./CreatorZusatz";
import { Preisliste } from "../anfragen/Preisliste";
import { t, ta, sprachName } from "../../../lib/sprache";
import { TarifHinweis, insightsVorbereitungText, useInsightsBereit, useModul } from "../../../components/Tarif";
import { chatLink } from "../../kommunikation/nachrichten/model";

/** Creator-Profil 360° (11_app_agentur/creator-profil.html, Umfang AD-031 des Altportals). */
export function CreatorProfil() {
  const { talentId = "" } = useParams();
  const ws = useStaffWorkspace();
  const { base, error, loading, noAccess, retry } = useBase(ws);
  const profile = useMemo(() => (base ? talentProfile(base, talentId) : null), [base, talentId]);

  if (error) return <ErrorState error={error} retry={retry} />;
  if (noAccess) return <NoAccess />;
  if (loading || !base) return <Loading />;
  if (!profile) return <NoAccess text={t("Diesen Creator gibt es nicht oder er ist für dein Konto nicht freigegeben.", "This creator does not exist or is not available to your account.")} />;
  return <Profil ws={ws} base={base} p={profile} />;
}

function Profil({ ws, base, p }: { ws: StaffWorkspace; base: Base; p: TalentProfile }) {
  const tl = p.talent;
  const [edit, setEdit] = useState(false);
  const plan = usePlan(ws);
  // Recht „Beträge sehen“ und „Kennzahlen und Agenturumsatz sehen“ (Befund 9)
  const { amounts, financials: finanzSicht } = useSicht();
  // Creator-Konto (0178): eigenes Profil, ohne Agenturanteil, Zugang, Chat und Creator-Liste
  const ich = !!ws.isCreator;
  const financials = finanzSicht && !ich;
  const year = base.today.slice(0, 4);
  const m = (c: number) => money(c, base.currency);
  const brandName = (id: string | null) => (id ? base.brandById.get(id)?.name ?? "–" : "–");
  const avgYear = p.year.deals ? Math.round(p.year.gross / p.year.deals) : 0;
  const age = ageOn(tl.birth_date, base.today);
  const handles = [tl.instagram_handle && `Instagram @${tl.instagram_handle}`, tl.tiktok_handle && `TikTok @${tl.tiktok_handle}`, tl.youtube_handle && `YouTube @${tl.youtube_handle}`].filter(Boolean).join(" · ");
  const address = [tl.street, [tl.postal_code, tl.city].filter(Boolean).join(" "), tl.country ? countryLabel(tl.country) : null].filter(Boolean).join(", ");

  return (
    <main id="main" className="content tk-detail tk-sd">
      <div className="pagehead">
        <div>
          <div className="eyebrow">{ich ? t("Mein Profil", "My profile") : ta("Agentur-Arbeitsbereich", "Agency workspace", "Dein Creator-Konto", "Your creator account")}</div>
          <h1>{p.name}<BeispielEtikett von={tl} /></h1>
          <p>
            {[tl.talent_number && !ich ? t(`Creator Nr. ${tl.talent_number}`, `Creator no. ${tl.talent_number}`) : null, tl.nickname ? t(`Rufname „${tl.nickname}“`, `Nickname “${tl.nickname}”`) : null, tl.city,
              p.since && !ich ? t(`Zusammenarbeit seit ${monthLabel(p.since)}`, `Working together since ${monthLabel(p.since)}`) : null].filter(Boolean).join(" · ")}
          </p>
        </div>
        <div className="row">
          {!ich && <Link className="btn" to="/agentur/creator">{t("Alle Creator", "All creators")}</Link>}
          <Link className="btn" to={`/agentur/creator/${tl.id}/mediakit`}>{t("Media-Kit", "Media kit")}</Link>
          <button className="" onClick={() => setEdit(true)}>{t("Bearbeiten", "Edit")}</button>
        </div>
      </div>

      {tl.status === "archived" && (
        <div className="notice">
          {t("Archiviert · Der Creator hat keinen Zugriff mehr auf das Portal dieser Agentur. Seine Umsätze bleiben in allen Summen enthalten.", "Archived · The creator no longer has access to this agency's portal. Their revenue remains included in all totals.")}{(tl as { post_term_until?: string | null }).post_term_until ? t(` Nachprovision nach der Nachlaufregel bis ${date((tl as { post_term_until?: string | null }).post_term_until!)}; betroffene Kooperationen tragen den Hinweis.`, ` Post-term commission under the post-term rule until ${date((tl as { post_term_until?: string | null }).post_term_until!)}; affected deals carry the notice.`) : ""}
        </div>
      )}
      {!ich && <MailZustellung talentId={tl.id} email={tl.email} timeZone={ws.org.timezone} />}
      {p.overdue && (
        <div className="integrity-banner" role="alert">
          <p>
            <strong>{p.overdue.count === 1 ? t("1 Rechnung überfällig", "1 invoice overdue") : t(`${p.overdue.count} Rechnungen überfällig`, `${p.overdue.count} invoices overdue`)}</strong>{amounts ? t(` · Summe ${sumsLabel(p.overdue.sums, money)}`, ` · total ${sumsLabel(p.overdue.sums, money)}`) : ""}{t(", älteste seit ", ", oldest since ")}{p.overdue.oldestDays} {p.overdue.oldestDays === 1 ? t("Tag", "day") : t("Tagen", "days")}{p.overdue.oldestNumber ? ` (${p.overdue.oldestNumber})` : ""}
          </p>
          <a className="btn" href="#rechnungen">Details</a>
        </div>
      )}

      <div className="grid tk-oben">
        <section className="panel stack">
          <h3>{t("Stammdaten", "Master data")}</h3>
          <div className="row">
            <span className="avatar">{initials(p.name)}</span>
            <div>
              <strong>{p.name}</strong>
              <p>{tl.email ?? t("Keine E-Mail hinterlegt", "No email on file")}</p>
            </div>
          </div>
          {!ich && <div className="row">
            {statusPills(tl, p.access).map((x) => <PillView key={x.label} pill={x} />)}
          </div>}
          <p>
            {amounts && !ich ? <>{t("Agenturanteil: ", "Agency share: ")}{bpLabel(p.commissionBp)}{p.commissionIsDefault ? t(" (Agentur-Standard)", " (agency default)") : ""} · </> : null}{base.currency} · {countryLabel(tl.country)}
            {!ich && <><br />{t("Bankdaten sieht nur, wer Auszahlungen ausführen darf.", "Only someone who may make payouts sees bank details.")}</>}
            {ich && <><br />{t("Anschrift, Steuer und Bankkonto für deine Rechnungen pflegst du unter Einstellungen · Firma.", "You maintain address, tax and bank account for your invoices under Settings · Company.")}</>}</p>
          <div>
            <Fact label={t("Anschrift", "Address")}>{address || "–"}</Fact>
            {tl.company_name && <Fact label={t("Firma", "Company")}>{[tl.company_name, tl.legal_form, tl.register_info].filter(Boolean).join(" · ")}</Fact>}
            <Fact label={t("Kanäle", "Channels")}>{handles || "–"}</Fact>
            <Fact label="WhatsApp">{tl.whatsapp_phone ? `${tl.whatsapp_phone} · ${tl.whatsapp_opt_in_at ? t(`Einwilligung seit ${date(tl.whatsapp_opt_in_at, ws.org.timezone)}`, `Consent since ${date(tl.whatsapp_opt_in_at, ws.org.timezone)}`) : t("ohne Einwilligung", "without consent")}` : "–"}</Fact>
            {!ich && <Fact label={t("Sprache", "Language")}>{tl.language === "en" || tl.language === "de" ? sprachName(tl.language) : t("Wie die Agentur", "Same as the agency")}</Fact>}
            {tl.birth_date && <Fact label={t("Geburtsdatum", "Date of birth")}>{date(tl.birth_date)}{age !== null ? t(` · ${age} Jahre`, ` · ${age} years`) : ""}</Fact>}
          </div>
          {age !== null && age < 18 && (
            <p className="notice" role="status">
              {t("Minderjährig: Verträge brauchen die Zustimmung der Erziehungsberechtigten", "Minor: contracts need the consent of the parents or guardians")}{tl.guardian_name ? ` (${tl.guardian_name}${tl.guardian_email ? `, ${tl.guardian_email}` : ""})` : ""}{t(". Nimm sie als weitere Unterzeichnende in den Vertrag auf.", ". Add them to the contract as further signatories.")}</p>
          )}
          {!ich && <Einwilligungen talentId={tl.id} timeZone={ws.org.timezone} />}
        </section>
        <section className="panel stack">
          <h3>{t("Zusammenarbeit", "Working together")}</h3>
          {amounts ? (
            <>
              <div className="metric num">{m(p.year.gross)}</div>
              <p>{t("Honorarvolumen netto · laufendes Jahr", "Fee volume net · current year")}</p>
            </>
          ) : (
            <>
              <div className="metric num">{p.activeDeals}</div>
              <p>{t("Aktive Kooperationen · ", "Active deals · ")}{p.deals.length} {t("insgesamt", "in total")}</p>
            </>
          )}
          {/* D-1: Aktionen nebeneinander statt untereinander */}
          {!ich && <div className="row">
            <Link className="btn " to={chatLink(tl.id)}>{t("Nachricht", "Message")}</Link>
            {/* Creator-Konto: eigenes Instagram lässt sich (noch) nicht verbinden, der Knopf liefe ins Leere */}
            <Link className="btn " to={`/agentur/creator/${tl.id}/insights`}>{t("Insights freigeben", "Share insights")}</Link>
          </div>}
        </section>
      </div>

      {amounts && (
        <section>
          <div className="group-label">{t("Laufzeit", "term")}</div>
          <div className="overview-kpis talent-kpis total">
            <Kpi label={t("Umsatz", "Revenue")} chip={p.since ? t(`seit ${monthLabel(p.since)}`, `since ${monthLabel(p.since)}`) : t("Laufzeit", "term")} value={m(p.lifetime.gross)}
              sub={t(`Kooperationsvolumen netto · ${p.lifetime.deals === 1 ? t("1 Kooperation", "1 deal") : t(`${p.lifetime.deals} Kooperationen`, `${p.lifetime.deals} deals`)} bis heute`, `Deal volume net · ${p.lifetime.deals === 1 ? t("1 Kooperation", "1 deal") : t(`${p.lifetime.deals} Kooperationen`, `${p.lifetime.deals} deals`)} to date`)} />
            {!ich && <Kpi label={t("Creator-Anteil", "Creator share")} chip={t("Laufzeit", "term")} value={m(p.lifetime.creator)} sub={t(`Davon verbucht ${m(p.lifetime.bookedCreator)}.`, `Of which booked ${m(p.lifetime.bookedCreator)}.`)} />}
            {financials && <Kpi label={t("Agenturanteil", "Agency share")} chip={t("Laufzeit", "term")} value={m(p.lifetime.agency)} sub={t(`Provision und Gebühren · davon Gebühren ${m(p.lifetime.fee)}`, `Commission and fees · of which fees ${m(p.lifetime.fee)}`)} />}
          </div>
        </section>
      )}
      {amounts && (
        <section>
          <div className="group-label">{t("Laufendes Jahr · ", "Current year · ")}{year}</div>
          <div className="overview-kpis period">
            <Kpi label={t("Honorarvolumen", "Fee volume")} chip={year} value={m(p.year.gross)} sub={t("Netto · verbucht und geplant", "Net · booked and planned")} />
            {financials && <Kpi label={t("Agenturanteil", "Agency share")} chip={year} value={m(p.year.agency)} sub={t(`Netto · davon verbucht ${m(p.year.bookedAgency)}`, `Net · of which booked ${m(p.year.bookedAgency)}`)} />}
            <Kpi label={t("Ø Kooperationswert", "Ø deal value")} chip={year} value={m(avgYear)} sub={t(`${p.year.deals === 1 ? t("1 Kooperation", "1 deal") : t(`${p.year.deals} Kooperationen`, `${p.year.deals} deals`)} · netto`, `${p.year.deals === 1 ? t("1 Kooperation", "1 deal") : t(`${p.year.deals} Kooperationen`, `${p.year.deals} deals`)} · net`)} />
            <Kpi label={t("Geplant", "Planned")} chip={t("ab nächstem Monat", "from next month")} value={m(p.planned.gross)} sub={t("Kooperationsvolumen netto, noch nicht erbracht", "Deal volume net, not yet delivered")} />
          </div>
        </section>
      )}
      <section>
        <div className="group-label">{t("Weitere", "Other")}</div>
        <div className="overview-kpis talent-kpis more">
          {amounts && (
            <Kpi label={t("Offene Forderungen", "Open receivables")} value={p.open.count ? sumsLabel(p.open.sums, money) : m(0)}
              sub={t(`${p.open.count === 1 ? t("1 Rechnung", "1 invoice") : t(`${p.open.count} Rechnungen`, `${p.open.count} invoices`)} an Marken · brutto`, `${p.open.count === 1 ? t("1 Rechnung", "1 invoice") : t(`${p.open.count} Rechnungen`, `${p.open.count} invoices`)} to brands · gross`)} />
          )}
          <Kpi label={t("Aktive Kooperationen", "Active deals")} value={p.activeDeals} sub={t(`${p.deals.length} insgesamt · ${p.brandCount === 1 ? t("1 Marke", "1 brand") : t(`${p.brandCount} Marken`, `${p.brandCount} brands`)}`, `${p.deals.length} in total · ${p.brandCount === 1 ? t("1 Marke", "1 brand") : t(`${p.brandCount} Marken`, `${p.brandCount} brands`)}`)} />
          {amounts && !ich && (
            <Kpi label={t("Offene Auszahlungen", "Open payouts")} value={p.payouts.count ? sumsLabel(p.payouts.sums, money) : m(0)}
              sub={t(`${p.payouts.count === 1 ? t("1 freigegebene Rechnung", "1 approved invoice") : t(`${p.payouts.count} freigegebene Rechnungen`, `${p.payouts.count} approved invoices`)} des Creators${p.payouts.inReview ? t(` · ${p.payouts.inReview} in Prüfung`, ` · ${p.payouts.inReview} in review`) : ""}`, `${p.payouts.count === 1 ? t("1 freigegebene Rechnung", "1 approved invoice") : t(`${p.payouts.count} freigegebene Rechnungen`, `${p.payouts.count} approved invoices`)} of the creator${p.payouts.inReview ? t(` · ${p.payouts.inReview} in Prüfung`, ` · ${p.payouts.inReview} in review`) : ""}`)} />
          )}
        </div>
      </section>
      {!amounts ? <p className="source-note">{HIDDEN_AMOUNTS()}</p> : !financials && !ich ? <p className="source-note">{HIDDEN_FINANCIALS()}</p> : null}

      {amounts && (
        <div className="grid portal-charts">
          <section className="panel">
            <h3>{t("Umsatzverlauf (netto)", "Revenue trend (net)")}</h3>
            <RevenueLines
              title={t(`Umsatzverlauf ${p.name}, letzte zwölf Monate`, `Revenue trend ${p.name}, last twelve months`)}
              months={p.series.map((s) => s.month)}
              currency={base.currency}
              series={[
                // weiches Trennzeichen: der Spaltenkopf der Wertetabelle (halbe Breite) darf das lange Wort trennen
                { label: t("Kooperations\u00advolumen", "Deal volume"), values: p.series.map((s) => s.gross) },
                // Creator-Konto: der eigene Anteil ist der ganze Umsatz, eine zweite Linie wäre doppelt
                ...(!ich ? [{ label: t("Creator-Anteil", "Creator share"), values: p.series.map((s) => s.creator) }] : []),
                ...(financials ? [{ label: t("Agenturanteil", "Agency share"), values: p.series.map((s) => s.agency) }] : []),
              ]}
            />
          </section>
          {financials && (
            <section className="panel">
              <h3>{t("Top-Marken (Agenturanteil netto)", "Top brands (agency share net)")}</h3>
              <RankRows
                empty={t("Noch keine Umsätze mit Marken.", "No revenue with brands yet.")}
                rows={p.topBrands.slice(0, 5).map((r) => ({ key: r.id, label: <Link to={`/agentur/marken/${r.id}`}>{brandName(r.id)}</Link>, value: r.agency, display: short(r.agency, base.currency) }))}
              />
            </section>
          )}
        </div>
      )}

      <div className="grid tk-oben">
        {/* Creator-Konto: Steuerstatus gilt aus Einstellungen · Firma; Abzugsteuer betrifft nur Agenturen, die Creator bezahlen */}
        {!ich && <Steuerstatus ws={ws} talentId={tl.id} current={tl.small_business} country={tl.country} />}
        <Exklusivitaeten p={p} today={base.today} brandName={brandName} />
        {!ich && <Abzugsteuer ws={ws} talent={tl} />}
      </div>

      <h2>{t("Kooperationen", "Deals")}</h2>
      <DealTable lines={p.deals} partner="brand" caption={t(`Kooperationen von ${p.name}`, `Deals of ${p.name}`)} empty={t("Noch keine Kooperationen mit diesem Creator.", "No deals with this creator yet.")} />
      {/* 0072: Preisliste (nur mit „Beträge sehen“); Angebote befüllen sich daraus vor */}
      {amounts && <Preisliste talentId={tl.id} name={p.name} currency={base.currency} />}

      <h2>{ich ? t("Verträge", "Contracts") : t("Verträge, Rechnungen und Bank", "Contracts, invoices and bank")}</h2>
      {/* Creator-Konto: keine Auszahlungsdaten, kein Portal-Zugang und keine Betreuer (es gibt nur die eigene Person) */}
      {!ich && (
        <div className="grid tk-oben">
          <BankPanel ws={ws} p={p} requirements={(base.settings.talent_profile_requirements as string[] | null | undefined) ?? DEFAULT_PROFILE_REQUIREMENTS} />
          <AccessPanel ws={ws} p={p} />
        </div>
      )}
      {!ich && <BetreuungPanel ws={ws} talentId={tl.id} />}
      <div className="grid tk-oben">
        <section className="panel stack">
          <div className="row between">
            <h3>{t("Verträge · ", "Contracts · ")}{p.contracts.length}</h3>
            <Link className="btn" to="/agentur/vertraege">{t("Alle Verträge", "All contracts")}</Link>
          </div>
          <ContractList contracts={p.contracts} partnerName={(c) => brandName(c.brand_id)} empty={ta("Keine Verträge für diesen Creator.", "No contracts for this creator.", "Noch keine Verträge.", "No contracts yet.")} />
        </section>
        <NotePanel ws={ws} talentId={tl.id} />
      </div>

      <h2 id="rechnungen">{t("Rechnungen · ", "Invoices · ")}{p.invoices.length}</h2>
      <InvoiceTable
        views={p.invoices}
        caption={t(`Rechnungen von und zu ${p.name}`, `Invoices from and to ${p.name}`)}
        partnerLabel={t("Marke / Kooperation", "Brand / deal")}
        partnerName={(v) => {
          const d = v.row.deal_id ? base.dealById.get(v.row.deal_id) : undefined;
          return [brandName(v.row.brand_id ?? d?.brand_id ?? null), d?.title].filter((x) => x && x !== "–").join(" · ") || "–";
        }}
        empty={t("Keine Rechnungen für diesen Creator.", "No invoices for this creator.")}
      />

      {amounts && !ich && (
        <Abrechnung p={p} today={base.today} brandName={brandName} views={base.views} periods={new Map(base.periods.map((x) => [x.id, x]))}
          agencyBp={base.settings.default_commission_bp ?? null} />
      )}

      {!ich && <InstagramPanel ws={ws} p={p} planName={plan.data?.planName ?? null} />}

      {flatTaxEnabled(base.settings) && <FlatTaxSection p={p} today={base.today} />}

      {p.bookings.length > 0 && (
        <>
          <h2>{t("Buchungen", "Bookings")}</h2>
          <BookingTable bookings={p.bookings} caption={t(`Buchungen von ${p.name}`, `Bookings of ${p.name}`)} partnerName={(b) => brandName(b.brand_id)}
            dealTitle={(b) => (b.deal_id ? base.dealById.get(b.deal_id)?.title ?? null : null)} />
        </>
      )}
      {base.unconverted > 0 && <p className="source-note">{ohneKursText(base.unconverted)}</p>}
      <p className="source-note">{t("Umsätze sind netto und gerechnet wie auf der Übersicht: Kooperationsmonate und Buchungen, verbucht und vorläufig; „Laufzeit“ reicht bis zum laufenden Monat.", "Revenue is net and calculated as in the overview: deal months and bookings, booked and provisional; “Lifetime” runs to the current month.")}</p>
      <DemoNote />
      {edit && <TalentDialog talent={tl} base={base} onClose={() => setEdit(false)} />}
    </main>
  );
}

function short(cents: number, currency: string): string {
  return moneyWhole(cents, currency);
}

// ── Betreut von (manager_talent_assignments): sehen alle, zuweisen der Admin ─────────────

function BetreuungPanel({ ws, talentId }: { ws: StaffWorkspace; talentId: string }) {
  const team = useTeamInfo(ws);
  const { busy, error, run } = useAction();
  const managers = managersOf(team.data, talentId);
  const assigned = new Set((team.data?.assignments ?? []).filter((a) => a.talent_id === talentId).map((a) => a.membership_id));
  return (
    <section className="panel stack" aria-labelledby="betreuung-title">
      <h3 id="betreuung-title">{t("Betreut von", "Managed by")}</h3>
      <p>{managers.length ? t(`Zugewiesen: ${managers.join(", ")}.`, `Assigned: ${managers.join(", ")}.`) : t("Niemand ist fest zugewiesen.", "Nobody is permanently assigned.")} {t("Admins und Manager mit Sicht auf alle Creator sehen den Creator ohnehin.", "Admins and managers who see all creators see the creator anyway.")}</p>
      {ws.isChef && team.data && (
        <div className="row" role="group" aria-label={t("Zuständige Manager", "Responsible managers")}>
          {team.data.members.filter((m) => m.role !== "chef_manager").map((m) => (
            <label key={m.id} className="row">
              <input type="checkbox" checked={assigned.has(m.id)} disabled={!!busy}
                onChange={(e) => void run(`zuweisen-${m.id}`, () => setAssignment(api, ws.org.org_id, m.id, talentId, e.target.checked),
                  e.target.checked ? t(`${m.name} betreut den Creator.`, `${m.name} manages the creator.`) : t(`${m.name} betreut den Creator nicht mehr.`, `${m.name} no longer manages the creator.`)).then(() => team.refetch())} />
              {m.name}
            </label>
          ))}
        </div>
      )}
      {error && <p className="tk-error" role="alert">{error}</p>}
    </section>
  );
}

// ── Bank- und Rechnungsprofil (Auszahlungsdaten nur mit Recht, ändern nur Admins) ──────────

function BankPanel({ ws, p, requirements }: { ws: StaffWorkspace; p: TalentProfile; requirements: string[] }) {
  const tl = p.talent;
  const canSee = ws.can("trigger_payouts");
  const payout = usePayout(ws.org.org_id, tl.id, canSee);
  const [edit, setEdit] = useState(false);
  const gaps = profileGaps(tl, canSee ? (payout.data ?? null) : undefined, requirements);
  const method = payout.data?.payout_method ?? "sepa";
  return (
    <section className="panel stack">
      <h3>{t("Bank- und Rechnungsprofil", "Bank and invoice profile")}</h3>
      {!canSee && <p id="bank-recht">{t("Auszahlungsdaten sieht nur, wer Auszahlungen ausführen darf. Wende dich an deinen Admin.", "Only someone who may make payouts sees payout details. Contact your admin.")}</p>}
      {canSee && payout.isPending && <p className="muted">{t("Wird geladen …", "Loading …")}</p>}
      {canSee && payout.error && <p className="tk-error" role="alert">{t("Die Auszahlungsdaten konnten nicht geladen werden.", "The payout details could not be loaded.")}</p>}
      {canSee && !payout.isPending && !payout.error && (
        payout.data ? (
          <div>
            <Fact label={t("Auszahlungsweg", "Payout method")}>{PAYOUT_METHODS.find((m) => m.value === method)?.label ?? method}{payout.data.payout_currency ? t(` · in ${payout.data.payout_currency}`, ` · in ${payout.data.payout_currency}`) : ""}</Fact>
            {method === "paypal" || method === "wise" ? (
              <Fact label={method === "paypal" ? t("PayPal-Adresse", "PayPal address") : t("Wise-Adresse", "Wise address")}>{payout.data.payout_email ?? "–"}</Fact>
            ) : (
              <>
                <Fact label={t("Kontoinhaber", "Account holder")}>{payout.data.bank_account_holder ?? "–"}</Fact>
                <Fact label="IBAN"><span className="mono">{payout.data.iban ?? "–"}</span></Fact>
                {method === "swift" && <Fact label={t("Kontonummer · Routing", "Account number · routing")}><span className="mono">{[payout.data.account_number, payout.data.routing_code].filter(Boolean).join(" · ") || "–"}</span></Fact>}
                <Fact label="BIC"><span className="mono">{payout.data.bic ?? "–"}</span></Fact>
                <Fact label="Bank">{payout.data.bank_name ?? "–"}</Fact>
                <Fact label={t("Bankland", "Bank country")}>{countryLabel(payout.data.bank_country)}</Fact>
              </>
            )}
          </div>
        ) : <p className="muted">{t("Noch keine Bankdaten hinterlegt.", "No bank details on file yet.")}</p>
      )}
      <div>
        {/* Begriffe nach dem Land des Creators (SAAS-R3-6) */}
        <Fact label={taxIdWord(tl.country)}>{tl.vat_id ?? "–"}</Fact>
        <Fact label={taxNumberWord(tl.country)}>{tl.tax_number ?? "–"}</Fact>
        <Fact label={smallBusinessLabel(tl.country)}>{tl.small_business ? t("Ja", "Yes") : t("Nein", "No")}</Fact>
      </div>
      {gaps.length ? <p>{t("Für Rechnungen fehlen: ", "Missing for invoices: ")}{gaps.join(", ")}.</p> : <p>{t("Alle Angaben für Rechnungen sind vorhanden.", "All details for invoices are available.")}</p>}
      {tl.small_business && tl.country === "DE" && !tl.tax_number && !tl.vat_id && (
        <small>{t("Hinweis: Auch Kleinunternehmer nennen auf Rechnungen ihre Steuernummer oder USt-IdNr. (§ 34a UStDV).", "Note: small businesses also state their tax number or VAT ID on invoices (§ 34a UStDV).")}</small>
      )}
      {canSee && (
        ws.isChef
          ? <div className="row"><button className="" onClick={() => setEdit(true)}>{t("Bankdaten bearbeiten", "Edit bank details")}</button></div>
          : <><div className="row"><button className="" disabled aria-describedby="bank-aendern-recht">{t("Bankdaten bearbeiten", "Edit bank details")}</button></div><p id="bank-aendern-recht">{RIGHTS_TEXT()}</p></>
      )}
      {edit && <PayoutDialog ws={ws} talentId={tl.id} name={p.name} payout={payout.data ?? null} onClose={() => setEdit(false)} />}
      <SelfBilling ws={ws} talentId={tl.id} name={p.name} agreedOn={(tl as { self_billing_agreed_on?: string | null }).self_billing_agreed_on ?? null}
        note={(tl as { self_billing_note?: string | null }).self_billing_note ?? null} />
      {(tl as { self_billing_agreed_on?: string | null }).self_billing_agreed_on && (
        <SelfBillingEInvoice ws={ws} talentId={tl.id} name={p.name} talent={tl as unknown as SelfBillingTalent} />
      )}
    </section>
  );
}

/**
 * Gutschriftverfahren (0046): Die Agentur rechnet für den Creator ab, statt eine Rechnung von ihm anzufordern. Das setzt eine
 * vorherige Vereinbarung voraus (z. B. im Rahmenvertrag); ohne sie lässt sich keine Gutschrift festschreiben.
 */
function SelfBilling({ ws, talentId, name, agreedOn, note }: { ws: StaffWorkspace; talentId: string; name: string; agreedOn: string | null; note: string | null }) {
  const [edit, setEdit] = useState(false);
  const [day, setDay] = useState(agreedOn ?? "");
  const [text, setText] = useState(note ?? "");
  const { busy, error, attempt } = useAction();
  // Tarifstaffel (0099): eine neue Vereinbarung erst mit „Auszahlungsläufe und Gutschriften“; bestehende bleiben änderbar
  const neuGesperrt = useModul("module_payouts").gesperrt && !agreedOn;
  const save = (clear: boolean) => attempt("gutschrift", () => api.update("talents", [["id", "eq", talentId]], {
    self_billing_agreed_on: clear ? null : day || null, self_billing_note: clear ? null : text.trim() || null,
  }), clear ? t("Gutschriftverfahren beendet.", "Self-billing ended.") : t("Vereinbarung gespeichert.", "Agreement saved.")).then((ok) => { if (ok) setEdit(false); });
  return (
    <div className="stack">
      <h4>{t("Gutschriftverfahren", "Self-billing")}</h4>
      <p>{agreedOn
        ? <>{t("Vereinbart am ", "Agreed on ")}{date(agreedOn, ws.org.timezone)}{note ? ` · ${note}` : ""}{t(". Die Agentur kann für ", ". The agency can issue self-billing invoices for ")}{name} {t("Gutschriften ausstellen (Rechnungen · Rechnung erstellen · Gutschrift); ", "(Invoices · Create invoice · Self-billing invoice); ")}{name} {t("kann im Portal widersprechen.", "can object in the portal.")}</>
        : <>{t("Nicht vereinbart. ", "Not agreed. ")}{name} {t("stellt eigene Rechnungen. Mit einer Vereinbarung (etwa im Rahmenvertrag) rechnet die Agentur per Gutschrift ab.", "issues their own invoices. With an agreement (e.g. in the framework contract) the agency settles by self-billing invoice.")}</>}</p>
      {ws.isChef && !edit && <div className="row"><button disabled={neuGesperrt} aria-describedby={neuGesperrt ? `gutschrift-tarif-${talentId}` : undefined} onClick={() => setEdit(true)}>{agreedOn ? t("Vereinbarung bearbeiten", "Edit agreement") : t("Vereinbarung eintragen", "Enter agreement")}</button></div>}
      {neuGesperrt && <TarifHinweis modul="module_payouts" id={`gutschrift-tarif-${talentId}`} was={t("Gutschriftverfahren", "Self-billing")} />}
      {edit && (
        <form className="stack" onSubmit={(e) => { e.preventDefault(); if (!day) return; void save(false); }}>
          <label>{t("Vereinbart am", "Agreed on")}<input type="date" required value={day} onChange={(e) => setDay(e.target.value)} /></label>
          <label>{t("Grundlage (optional)", "Basis (optional)")}<input value={text} maxLength={500} onChange={(e) => setText(e.target.value)} placeholder={t("z. B. Rahmenvertrag V-0012, § 5", "e.g. framework contract V-0012, § 5")} /></label>
          {error && <p className="tk-error" role="alert">{error}</p>}
          <div className="row">
            <button className="primary" type="submit" disabled={!!busy}>{t("Speichern", "Save")}</button>
            {agreedOn && <button type="button" className="danger" disabled={!!busy} onClick={() => void save(true)}>{t("Verfahren beenden", "End self-billing")}</button>}
            <button type="button" onClick={() => setEdit(false)}>{t("Abbrechen", "Cancel")}</button>
          </div>
        </form>
      )}
    </div>
  );
}

type SelfBillingTalent = { country: string | null; small_business: boolean | null; self_billing_e_invoice?: boolean | null; whatsapp_phone?: string | null };

/** Warum die Vorgabe so ausfällt, in Klartext (K-417c) */
function eInvoiceReasonText(reason: SelfBillingEInvoiceReason, from: string): string {
  switch (reason) {
    case "auto_on": return t(`Vorgabe: mit E-Rechnung (Creator in Deutschland, regelbesteuert; seit ${date(from)}).`, `Default: with e-invoice (creator in Germany, standard VAT; since ${date(from)}).`);
    case "before_cutoff": return t(`Vorgabe: ab ${date(from)} mit E-Rechnung (Creator in Deutschland, regelbesteuert), bis dahin nur PDF.`, `Default: with e-invoice from ${date(from)} (creator in Germany, standard VAT), only PDF until then.`);
    case "small_business": return t("Vorgabe: nur PDF (Kleinunternehmer).", "Default: PDF only (small business).");
    case "creator_abroad": return t("Vorgabe: nur PDF (Creator nicht in Deutschland, z. B. Österreich oder Schweiz).", "Default: PDF only (creator not in Germany, e.g. Austria or Switzerland).");
    case "agency_abroad": return t("Vorgabe: nur PDF (Agentur nicht in Deutschland).", "Default: PDF only (agency not in Germany).");
    default: return "";
  }
}

/**
 * K-417c (0335): Gutschrift zusätzlich als E-Rechnung (XRechnung). Vorgabe automatisch nach Land und Steuerstatus des Creators
 * ab dem Stichtag der Agentur (Einstellungen · Nummernkreis); hier je Creator überschreibbar. Fehlen Pflichtangaben (z. B.
 * Telefon des Creators für die XRechnung), geht die Gutschrift-Mail nicht raus und der Postausgang nennt, was fehlt.
 */
function SelfBillingEInvoice({ ws, talentId, name, talent }: { ws: StaffWorkspace; talentId: string; name: string; talent: SelfBillingTalent }) {
  const orgId = ws.org.org_id;
  const settings = useQuery({
    queryKey: ["org_settings", orgId, "self_billing_e_invoice"],
    queryFn: () => api.select<{ self_billing_e_invoice_from: string | null }>("org_settings", { columns: "self_billing_e_invoice_from", filters: [["org_id", "eq", orgId]] }).then((r) => r[0] ?? null),
  });
  const { busy, error, attempt } = useAction();
  const choice = talent.self_billing_e_invoice ?? null;
  const today = new Date().toISOString().slice(0, 10);
  const auto = selfBillingEInvoiceDecision({
    agencyFrom: settings.data?.self_billing_e_invoice_from ?? null, creatorCountry: talent.country, agencyCountry: ws.org.country ?? null,
    smallBusiness: talent.small_business, issueDate: today,
  });
  const value = choice === true ? "on" : choice === false ? "off" : "auto";
  const save = (v: string) => attempt("gs-xml", () => api.update("talents", [["id", "eq", talentId]], { self_billing_e_invoice: v === "on" ? true : v === "off" ? false : null }),
    t("Gespeichert.", "Saved."));
  return (
    <div className="stack">
      <label>
        {t("E-Rechnung zur Gutschrift", "E-invoice with self-billing invoices")}
        <select value={value} disabled={!ws.isChef || !!busy} onChange={(e) => void save(e.target.value)} aria-describedby={`gs-xml-${talentId}`}>
          <option value="auto">{t("Automatisch nach Land und Steuerstatus", "Automatic by country and tax status")}</option>
          <option value="on">{t("Immer mit E-Rechnung (XRechnung)", "Always with e-invoice (XRechnung)")}</option>
          <option value="off">{t("Nur PDF", "PDF only")}</option>
        </select>
      </label>
      <small id={`gs-xml-${talentId}`}>{eInvoiceReasonText(auto.reason, auto.from)}{" "}
        {(choice === true || (choice === null && auto.wanted)) && !talent.whatsapp_phone
          ? t(`Für die XRechnung braucht es eine Telefonnummer von ${name} (WhatsApp-Nummer im Profil).`, `The XRechnung needs a phone number for ${name} (WhatsApp number in the profile).`) : ""}</small>
      {error && <p className="tk-error" role="alert">{error}</p>}
    </div>
  );
}

function PayoutDialog({ ws, talentId, name, payout, onClose }: { ws: StaffWorkspace; talentId: string; name: string; payout: PayoutRow | null; onClose: () => void }) {
  const before = payoutFormFrom(payout);
  const [form, setForm] = useState<PayoutForm>(before);
  const [errors, setErrors] = useState<FormErrors<PayoutForm>>({});
  const [hints, setHints] = useState<string[]>([]);
  const { busy, error, run } = useAction();
  const set = <K extends keyof PayoutForm>(k: K, v: PayoutForm[K]) => setForm((f) => ({ ...f, [k]: v }));
  // K-534 b: Konten der Agentur, damit ein kopiertes Agenturkonto nicht als Auszahlungskonto des Creators landet
  const agentur = useQuery({ queryKey: ["org_bank_accounts", ws.org.org_id, "iban"], queryFn: () => api.select<{ iban: string }>("org_bank_accounts", { columns: "iban", filters: [["org_id", "eq", ws.org.org_id]] }).catch(() => []) });
  async function save(e: FormEvent) {
    e.preventDefault();
    const r = validatePayout(form, before, { agenturIbans: (agentur.data ?? []).map((a) => a.iban) });
    setErrors(r.errors);
    setHints(r.hints);
    if (!r.patch) return;
    const patch = r.patch;
    if (await run("bank", () => savePayout(api, ws.org.org_id, talentId, patch), t("Bankdaten gespeichert.", "Bank details saved."))) onClose();
  }
  return (
    <Dialog open onClose={onClose} title={t(`Bankdaten von ${name}`, `Bank details of ${name}`)}>
      <form className="stack" onSubmit={save} noValidate>
        <p>{t("IBAN und BIC werden formal geprüft. Im SEPA-Raum genügt die IBAN. Der Creator kann seine Bankdaten auch selbst im Profil pflegen.", "IBAN and BIC are checked formally. In the SEPA area the IBAN is enough. The creator can also maintain their bank details in their profile.")}</p>
        <SelectField id="pf-weg" label={t("Auszahlungsweg", "Payout method")} value={form.payout_method} onChange={(v) => set("payout_method", v)} options={PAYOUT_METHODS} />
        {form.payout_method === "paypal" || form.payout_method === "wise" ? (
          <TextField id="pf-mail" label={form.payout_method === "paypal" ? t("PayPal-Adresse", "PayPal address") : t("E-Mail des Wise-Kontos", "Email of the Wise account")} type="email" inputMode="email"
            value={form.payout_email} onChange={(v) => set("payout_email", v)} error={errors.payout_email} />
        ) : (
          <>
            <TextField id="pf-inhaber" label={t("Kontoinhaber", "Account holder")} value={form.bank_account_holder} onChange={(v) => set("bank_account_holder", v)} />
            <TextField id="pf-iban" label={form.payout_method === "swift" ? t("IBAN (falls vorhanden)", "IBAN (if available)") : "IBAN"} value={form.iban} onChange={(v) => set("iban", v)} error={errors.iban} />
            {form.payout_method === "swift" && (
              <div className="grid tk-oben">
                <TextField id="pf-konto" label={t("Kontonummer", "Account number")} value={form.account_number} onChange={(v) => set("account_number", v)} error={errors.account_number} hint={t("Für Länder ohne IBAN, z. B. USA, Kanada, Australien.", "For countries without IBAN, e.g. USA, Canada, Australia.")} />
                <TextField id="pf-routing" label={t("Routing / Bankleitzahl", "Routing / bank code")} value={form.routing_code} onChange={(v) => set("routing_code", v)} hint={t("ABA, Transit, BSB oder Sort Code.", "ABA, transit, BSB or sort code.")} />
              </div>
            )}
            <TextField id="pf-bic" label={form.payout_method === "swift" ? "BIC (SWIFT)" : t("BIC (nur außerhalb des SEPA-Raums nötig)", "BIC (only needed outside the SEPA area)")} value={form.bic} onChange={(v) => set("bic", v)} error={errors.bic} />
            <TextField id="pf-bank" label="Bank" value={form.bank_name} onChange={(v) => set("bank_name", v)} />
            <SelectField id="pf-land" label={t("Bankland", "Bank country")} value={form.bank_country} onChange={(v) => set("bank_country", v)} error={errors.bank_country}
              options={[{ value: "", label: t("Nicht angegeben", "Not specified") }, ...countryOptions().map((c) => ({ value: c.code, label: c.name }))]} />
          </>
        )}
        <SelectField id="pf-waehrung" label={t("Auszahlungswährung", "Payout currency")} value={form.payout_currency} onChange={(v) => set("payout_currency", v)}
          options={[{ value: "", label: t("Wie die Rechnung", "Same as the invoice") }, ...CURRENCIES.map((c) => ({ value: c, label: c }))]} />
        {hints.length > 0 && <p>{hints.join(" ")}</p>}
        {error && <p className="tk-error" role="alert">{error}</p>}
        <div className="row">
          <button className="primary" type="submit" disabled={!!busy} aria-busy={!!busy}>{busy ? t("Wird gespeichert …", "Saving …") : t("Bankdaten speichern", "Save bank details")}</button>
          <button type="button" onClick={onClose}>{t("Abbrechen", "Cancel")}</button>
        </div>
      </form>
    </Dialog>
  );
}

// ── Konto und Einladung (ersetzt Nutzername und Passwort des Altportals) ─────────────────────────

function AccessPanel({ ws, p }: { ws: StaffWorkspace; p: TalentProfile }) {
  const tl = p.talent;
  const a = p.access;
  const tz = ws.org.timezone;
  const { busy, error, run } = useAction();
  const [result, setResult] = useState<InvitationResult | null>(null);
  const archived = tl.status === "archived";
  const inv = a.invitation;
  const stamp = (v: string | null | undefined) => (v ? t(`${date(v, tz)}, ${time(v, tz)} Uhr`, `${date(v, tz)}, ${time(v, tz)}`) : "–");

  async function invite() {
    const r = await run("einladen", () => inviteTalent(api, tl.id), t("Einladung erstellt.", "Invitation created."));
    if (r) setResult(r);
  }
  async function resend(id: string) {
    const r = await run("neu", () => resendInvitation(api, id), t("Einladung neu gesendet.", "Invitation resent."));
    if (r) setResult(r);
  }

  return (
    <section className="panel stack">
      <h3>{t("Konto und Einladung", "Account and invitation")}</h3>
      <div className="row"><PillView pill={a.pill} /></div>
      {a.state === "linked" && (
        <p>
          {t("Der Creator meldet sich mit eigenem Konto an (verbunden seit ", "The creator signs in with their own account (connected since ")}{date(a.link?.created_at, tz)}).
          {p.presence?.last_seen_at ? t(` Zuletzt im Portal: ${stamp(p.presence.last_seen_at)}.`, ` Last in the portal: ${stamp(p.presence.last_seen_at)}.`) : t(" Noch keine Anmeldung erfasst.", " No sign-in recorded yet.")}
        </p>
      )}
      {a.state === "invited" && inv && <p>{t("Einladung an ", "Invitation to ")}{inv.email}{t(", gültig bis ", ", valid until ")}{date(inv.expires_at, tz)}.</p>}
      {a.state === "expired" && inv && <p>{t("Die Einladung an ", "The invitation to ")}{inv.email} {t("ist am ", "expired on ")}{date(inv.expires_at, tz)} {t("abgelaufen.", ".")}</p>}
      {a.state === "revoked" && inv && <p>{t("Die Einladung an ", "The invitation to ")}{inv.email} {t("wurde am ", "was withdrawn on ")}{date(inv.revoked_at, tz)} {t("zurückgezogen.", ".")}</p>}
      {a.state === "none" && <p>{t("Noch kein Portal-Zugang. Mit der Einladung legt der Creator selbst ein Passwort fest; die Agentur sieht es nie.", "No portal access yet. With the invitation the creator sets their own password; the agency never sees it.")}</p>}
      {archived && <p>{t("Archivierte Creator haben keinen Zugriff; einladen lässt sich erst nach dem Reaktivieren.", "Archived creators have no access; inviting is only possible after reactivating.")}</p>}

      {result && (
        <div className="notice stack" role="status">
          <p>{t("Der Link ist bis ", "The link is valid until ")}{date(result.expires_at, tz)} {t("gültig. Die E-Mail geht über den Postausgang raus", ". The email goes out via the outbox")}{api.mode === "demo" ? t(" (in der Demo werden keine E-Mails verschickt)", " (in the demo no emails are sent)") : ""}.</p>
          <CopyLink label={t("Einladungslink zum Weitergeben", "Invitation link to pass on")} url={invitationLink(window.location.origin, result.token)} />
        </div>
      )}
      {error && <p className="tk-error" role="alert">{error}</p>}

      {!archived && (
        <div className="row">
          {(a.state === "none" || a.state === "expired" || a.state === "revoked") && (
            <>
              <button className="primary" disabled={!tl.email || !!busy} aria-describedby={tl.email ? undefined : "einladen-ohne-mail"} onClick={() => void invite()}>
                {busy === "einladen" ? t("Wird gesendet …", "Sending …") : a.state === "none" ? t("Einladen", "Invite") : t("Erneut einladen", "Invite again")}
              </button>
              {!tl.email && <small id="einladen-ohne-mail">{t("Erst eine E-Mail-Adresse hinterlegen (Bearbeiten).", "First enter an email address (Edit).")}</small>}
            </>
          )}
          {a.state === "invited" && inv && (
            <>
              <button className="" disabled={!!busy} onClick={() => void resend(inv.id)}>{busy === "neu" ? t("Wird gesendet …", "Sending …") : t("Neu senden", "Resend")}</button>
              <ConfirmAction label={t("Zurückziehen", "Withdraw")} confirmLabel={t("Ja, zurückziehen", "Yes, withdraw")} busy={busy === "weg"}
                question={t(`Einladung an ${inv.email} zurückziehen? Der Link funktioniert danach nicht mehr.`, `Withdraw invitation to ${inv.email}? The link will then no longer work.`)}
                onConfirm={() => run("weg", () => revokeInvitation(api, inv.id), t("Einladung zurückgezogen.", "Invitation withdrawn."))} />
            </>
          )}
          {a.state === "linked" && (
            ws.isChef ? (
              <ConfirmAction danger label={t("Zugang trennen", "Disconnect access")} confirmLabel={t("Ja, trennen", "Yes, disconnect")} busy={busy === "trennen"}
                question={t("Zugang trennen? Der Creator kann sich danach nicht mehr in dieser Agentur anmelden; Daten bleiben erhalten. Eine neue Einladung stellt den Zugang wieder her.", "Disconnect access? The creator can then no longer sign in to this agency; data is kept. A new invitation restores access.")}
                onConfirm={() => run("trennen", () => removeAccess(api, tl.id), t("Zugang getrennt.", "Access disconnected."))} />
            ) : (
              <>
                <button className="" disabled aria-describedby="zugang-recht">{t("Zugang trennen", "Disconnect access")}</button>
                <small id="zugang-recht">{RIGHTS_TEXT()}</small>
              </>
            )
          )}
        </div>
      )}
      <Link to="/agentur/creator/einladen">{t("Alle Einladungen", "All invitations")}</Link>
    </section>
  );
}

// ── Interne Notiz (nie für den Creator sichtbar) ─────────────────────────────────────────────────

function NotePanel({ ws, talentId }: { ws: StaffWorkspace; talentId: string }) {
  const note = useTalentNote(ws.org.org_id, talentId);
  const [text, setText] = useState<string | null>(null);
  const { busy, error, run } = useAction();
  const saved = note.data?.note ?? "";
  const value = text ?? saved;
  const dirty = text !== null && text !== saved;
  return (
    <section className="panel stack">
      <h3>{t("Interne Notiz", "Internal note")}</h3>
      <label>
        {t("Notiz", "Note")}<textarea value={value} onChange={(e) => setText(e.target.value)} disabled={note.isPending} aria-describedby="notiz-hinweis" placeholder={t("Absprachen, Vorlieben, Erreichbarkeit …", "Agreements, preferences, availability …")} />
      </label>
      <small id="notiz-hinweis">{ta("Nur für das Team sichtbar, nie für den Creator.", "Only visible to the team, never to the creator.", "Nur für dich sichtbar, nie für Marken.", "Only visible to you, never to brands.")}{note.data ? t(` Zuletzt geändert am ${date(note.data.updated_at, ws.org.timezone)}.`, ` Last changed on ${date(note.data.updated_at, ws.org.timezone)}.`) : ""}</small>
      {error && <p className="tk-error" role="alert">{error}</p>}
      <div className="row">
        <button className="primary" disabled={!dirty || !!busy} aria-busy={!!busy}
          onClick={async () => { if (await run("notiz", () => saveTalentNote(api, ws.org.org_id, talentId, value), t("Notiz gespeichert.", "Note saved."))) setText(null); }}>
          {busy ? t("Wird gespeichert …", "Saving …") : t("Notiz speichern", "Save note")}
        </button>
      </div>
    </section>
  );
}

// ── Instagram (nur verbunden und mit Tarifmodul; Freischaltung AD-194) ───────────────────────────

function InstagramPanel({ ws, p, planName }: { ws: StaffWorkspace; p: TalentProfile; planName: string | null }) {
  const tl = p.talent;
  const ig = useIg(ws.org.org_id, tl.id);
  // Tarifmodul für alle Rollen lesbar (org_entitlements), nicht nur für den Admin
  const ent = useEntitlements(ws.org.org_id);
  const moduleOff = ent.data ? (ent.data.limits.module_insights ?? 0) < 1 : false;
  // K-375: Instagram-Insights in Vorbereitung (Meta-Prüfung): kein Tarifhinweis, kein Upgrade
  const insightsBereit = useInsightsBereit();
  const archived = tl.status === "archived";
  const { busy, error, run } = useAction();
  const account = ig.data?.account ?? null;
  const totals = ig.data ? latestTotals(ig.data.periodTotals, "30d") : null;
  const consent = consentInfo(ig.data?.consent ?? null);
  const activeShares = (ig.data?.shares ?? []).filter((s) => shareState(s, Date.now()) === "active").length;
  const zeitpunkt = (iso: string) => `${date(iso, ws.org.timezone)}, ${time(iso, ws.org.timezone)}`;
  const erlauben = (
    <button className="tk-ig-knopf" disabled={!!busy || moduleOff || archived} aria-busy={!!busy} aria-describedby={moduleOff ? "ig-modul-hinweis" : undefined}
      onClick={() => void run("ig", () => setIgEnabled(api, tl.id, !tl.ig_enabled), tl.ig_enabled ? t("Verbinden nicht mehr erlaubt.", "Connecting no longer allowed.") : t("Verbinden erlaubt.", "Connecting allowed."))}>
      {tl.ig_enabled ? t("Verbinden nicht mehr erlauben", "No longer allow connecting") : t("Verbinden erlauben", "Allow connecting")}
    </button>
  );
  const freigeben = !archived && <Link className="btn tk-ig-knopf" to={`/agentur/creator/${tl.id}/insights`}>{t("Insights freigeben", "Share insights")}</Link>;
  return (
    <section className="panel stack tk-ig-verbindung">
      {/* D-2 (Inhaber 21.10.2026): verbunden als eine Zeile (Bild, @name, Stand, Zeitpunkt des Abgleichs, Aktionen) statt Überschrift,
          Pille und Satz; „Verbunden seit …“ im Tooltip */}
      {account && !moduleOff ? (
        <div className="tk-ig-zeile">
          <span className="tk-ig-bild" aria-hidden="true">{(account.username || "?").slice(0, 1).toUpperCase()}</span>
          <span className="tk-ig-wer">
            <strong className="tk-ig-name"><span>Instagram</span> @{account.username}</strong>
            <span className={`tk-ig-stand tk-ig-stand-${account.status === "active" ? "ok" : account.status === "expired" ? "warnung" : "fehler"}`}>
              {account.status === "active" && <span aria-hidden="true">✓ </span>}{igStatusPill(account).label}
            </span>
            <small className="tk-ig-zeit" title={t(`Verbunden seit ${date(account.connected_at, ws.org.timezone)}`, `Connected since ${date(account.connected_at, ws.org.timezone)}`)}>
              {account.last_sync_at ? t(`Stand ${zeitpunkt(account.last_sync_at)}`, `As of ${zeitpunkt(account.last_sync_at)}`) : t("noch kein Abgleich", "no sync yet")}
            </small>
          </span>
          <span className="tk-ig-aktionen">{freigeben}{erlauben}</span>
        </div>
      ) : (
        <div className="row between">
          <h3>Instagram</h3>
          <PillView pill={!insightsBereit && !account ? pill(t("In Vorbereitung", "In preparation"), "neutral") : moduleOff ? pill(t("Nicht im Tarif", "Not in plan"), "neutral") : igStatusPill(account)} />
        </div>
      )}
      {ig.isPending && <p className="muted">{t("Wird geladen …", "Loading …")}</p>}
      {ig.error && <p className="tk-error" role="alert">{t("Die Instagram-Daten konnten nicht geladen werden.", "The Instagram data could not be loaded.")}</p>}
      {!insightsBereit ? <p>{insightsVorbereitungText()}</p>
        : moduleOff && <p>{t("Das Modul Insights ist im Tarif ", "The Insights module is not included in the plan ")}{planName ?? ""} {t("nicht enthalten. Kennzahlen und Freigaben an Marken gibt es erst mit einem größeren Tarif.", ". Metrics and shares with brands are only available with a larger plan.")}<TarifWaehlen /></p>}
      {!moduleOff && !ig.isPending && !account && (
        <p>{t("Nicht verbunden. Der Creator verbindet sein Konto selbst im Creator-Portal", "Not connected. The creator connects their account themselves in the creator portal")}{tl.ig_enabled ? "." : t(", sobald die Agentur das Verbinden erlaubt.", ", as soon as the agency allows connecting.")}</p>
      )}
      {account && (
        <>
          <div className="overview-kpis period">
            <Kpi label="Follower" value={formatCount(account.followers_count)} sub={t(`${formatCount(account.media_count)} Beiträge`, `${formatCount(account.media_count)} posts`)} />
            {(["reach", "views", "interactions"] as const).map((k) => (
              <Kpi key={k} label={metricLabel(k)} chip={t("30 Tage", "30 days")} value={formatCount(totals?.metrics[k] ?? null)} sub={totals ? t(`Stand ${date(totals.computed_on)}`, `As of ${date(totals.computed_on)}`) : t("Noch kein Zeitraumwert von Meta", "No period value from Meta yet")} />
            ))}
          </div>
          {account.status === "expired" && <p>{t("Die Anmeldung bei Instagram ist abgelaufen; der Creator muss sich neu verbinden.", "The Instagram sign-in has expired; the creator must reconnect.")}</p>}
          {account.last_error && <p className="tk-error">{t("Letzter Fehler beim Abgleich: ", "Last sync error: ")}{account.last_error}</p>}
        </>
      )}
      <p className="muted">
        {consent.allowed
          ? t(`Weitergabe an Marken erlaubt für: ${consent.blocks.map(blockLabel).join(", ")}.`, `Sharing with brands allowed for: ${consent.blocks.map(blockLabel).join(", ")}.`)
          : t("Der Creator hat der Weitergabe an Marken derzeit nicht zugestimmt.", "The creator has currently not consented to sharing with brands.")}
        {` ${activeShares === 1 ? t("1 aktive Freigabe", "1 active share") : t(`${activeShares} aktive Freigaben`, `${activeShares} active shares`)}.`}
      </p>
      {error && <p className="tk-error" role="alert">{error}</p>}
      {archived && <p>{t("Archiviert: Freigaben sind zurückgezogen, der Abgleich mit Instagram ist beendet.", "Archived: shares have been withdrawn, the Instagram sync has ended.")}</p>}
      {!(account && !moduleOff) && <div className="row">{erlauben}{freigeben}</div>}
      {moduleOff && <small id="ig-modul-hinweis">{insightsBereit ? t("Im Tarif nicht enthalten: Verbinden lässt sich erst mit einem Tarif mit Insights erlauben.", "Not included in the plan: connecting can only be allowed with a plan that includes insights.") : t("In Vorbereitung: Verbinden lässt sich erlauben, sobald Instagram-Insights freigeschaltet sind.", "In preparation: connecting can be allowed as soon as Instagram insights are available.")}</small>}
    </section>
  );
}

// ── Pauschalversteuerung (Tabelle und CSV wie im Altportal, V130) ────────────────────────────────

function FlatTaxSection({ p, today }: { p: TalentProfile; today: string }) {
  const coops = new Set(p.flatTax.map((l) => l.dealId)).size;
  function download() {
    const blob = new Blob([flatTaxCsv(p.flatTax)], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `kooperationen-${fileSlug(p.name)}-${today}.csv`;
    document.body.append(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
  return (
    <>
      <div className="row between">
        <h2>{t("Pauschalversteuerung", "Flat-rate taxation")}</h2>
        <button className="" disabled={!p.flatTax.length} onClick={download}>{t("CSV herunterladen", "Download CSV")}</button>
      </div>
      <div className="tablewrap tk-cardtable">
        <table>
          <caption className="sr-only">{t("Pauschalversteuerung je Kooperation und Monat", "Flat-rate taxation per deal and month")}</caption>
          <thead>
            <tr>
              <th scope="col">{t("Kooperation", "Deal")}</th>
              <th scope="col">{t("Monat", "Month")}</th>
              <th scope="col">{t("Firma", "Company")}</th>
              <th scope="col">{t("Pauschal versteuert", "Flat-rate taxed")}</th>
            </tr>
          </thead>
          <tbody>
            {p.flatTax.map((l) => (
              <tr key={`${l.dealId}-${l.month ?? "ohne"}`}>
                <td data-label={t("Kooperation", "Deal")}><Link to={`/agentur/kooperationen/${l.dealId}${l.month ? `?monat=${l.month}` : ""}`}><strong>{l.title}</strong></Link></td>
                <td data-label={t("Monat", "Month")}>{l.monthLabel}</td>
                <td data-label={t("Firma", "Company")}>{l.company}</td>
                <td data-label={t("Pauschal versteuert", "Flat-rate taxed")}><PillView pill={l.flat ? pill(t("Ja", "Yes"), "info") : pill(t("Nein", "No"), "neutral")} /></td>
              </tr>
            ))}
            {!p.flatTax.length && <tr><td colSpan={4}>{t("Noch keine Kooperationen.", "No deals yet.")}</td></tr>}
          </tbody>
        </table>
      </div>
      <p className="source-note">
        {p.flatTax.length === 1 ? t("1 Zeile", "1 row") : t(`${p.flatTax.length} Zeilen`, `${p.flatTax.length} rows`)} · {coops === 1 ? t("1 Kooperation", "1 deal") : t(`${coops} Kooperationen`, `${coops} deals`)} {t("· Das Kennzeichen „Produkte pauschal versteuert“ gilt je Kooperation und ändert keine Berechnung.", "· The flag “products flat-rate taxed” applies per deal and does not change any calculation.")}</p>
    </>
  );
}

/**
 * CREATOR-2: Einwilligung der Erziehungsberechtigten beim Konto-Anlegen (0058, talent_guardian_consents), sichtbar am Profil.
 * Name und Adresse übernimmt 0079 zusätzlich in die Erziehungsberechtigten des Profils; die Eltern bekommen eine Hinweismail.
 */
function Einwilligungen({ talentId, timeZone }: { talentId: string; timeZone: string }) {
  const q = useQuery({
    queryKey: ["talent_guardian_consents", talentId],
    queryFn: () => api.select<{ id: string; guardian_name: string; guardian_email: string | null; statement_version: string; confirmed_at: string }>("talent_guardian_consents", {
      columns: "id, guardian_name, guardian_email, statement_version, confirmed_at", filters: [["talent_id", "eq", talentId]], order: [["confirmed_at", "desc"]],
    }),
  });
  if (!q.data?.length) return null;
  return (
    <div>
      {q.data.map((c) => (
        <Fact key={c.id} label={t("Einwilligung Erziehungsberechtigte", "Parent or guardian consent")}>
          {[c.guardian_name, c.guardian_email].filter(Boolean).join(", ")} {t("· erklärt beim Konto-Anlegen am ", "· declared when creating the account on ")}{date(c.confirmed_at, timeZone)} {t("· Fassung ", "· version ")}{c.statement_version}
        </Fact>
      ))}
    </div>
  );
}
